import React, { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import {
  buildFieldPayload,
  buildRowFieldPayload,
  isRowEditableSection,
  isFieldDirty,
} from "utilities/fieldEdit";
import { fieldPercentError } from "utilities/percentBounds";

/**
 * SOUND-643 Phase 2 — one record field, read or editing.
 *
 * The Salesforce pattern: a pencil per row, one field at a time. Deliberately
 * NOT the draft wizard — that surface carries "Submit for review", which on an
 * approved application would regress the status, overwrite `submittedAt`,
 * re-email the merchant and re-post to Slack.
 *
 * WHY NOT REUSE `FormField`
 * -------------------------
 * The first attempt did, on the reasoning that sharing the component stops
 * field-type handling from drifting. That was the wrong trade: FormField is
 * built for the merchant WIZARD — `mb-4` spacing, `text-sm font-light` labels, a
 * brand-coloured asterisk, `px-4 py-2` inputs — so editing a field made the row
 * jump into a different design language mid-record. Same mistake as reaching
 * for StepRenderer, one level down.
 *
 * What actually prevents drift is the SCHEMA, not the component. The types below
 * are exactly the eight the schema declares (text 28, select 19, number 13,
 * tel 5, select-other 4, date 4, email 4, textarea 4); anything unrecognised
 * falls through to a text input rather than failing to render.
 *
 * The label is styled identically in both modes, so the row does not shift when
 * it opens.
 */

const INPUT = "input py-1.5 text-sm";

function EditInput({ field, value, onChange, other, onOtherChange, autoFocus }) {
  const type = field.type || "text";
  const common = {
    id: `edit-${field.name}`,
    name: field.name,
    value: value ?? "",
    onChange: (e) => onChange(e.target.value),
    autoFocus,
    className: INPUT,
  };

  if (type === "textarea") {
    return <textarea {...common} rows={field.rows || 3} className={`${INPUT} resize-y`} />;
  }

  if (type === "select" || type === "select-other") {
    return (
      <>
        <select {...common} className="select py-1.5 text-sm">
          <option value="">Select…</option>
          {(field.options || []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {/* Mirrors FormField: the companion only appears on "Other". */}
        {type === "select-other" && value === "Other" && field.otherField && (
          <input
            type="text"
            value={other ?? ""}
            onChange={(e) => onOtherChange(e.target.value)}
            placeholder="Please specify…"
            className={`${INPUT} mt-1.5`}
          />
        )}
      </>
    );
  }

  const htmlType =
    type === "number" ? "number"
    : type === "date" ? "date"
    : type === "email" ? "email"
    : type === "tel" ? "tel"
    : "text";

  return <input {...common} type={htmlType} placeholder={field.placeholder || undefined} />;
}

export default function RecordFieldRow({ field, sectionKey, rowId, canEdit, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(field.value ?? "");
  const [other, setOther] = useState(field.otherValue ?? "");
  const [saving, setSaving] = useState(false);
  // A refusal from the API that names this field (SOUND-775: an off-list owner
  // title is a 400, a non-internal write a 403). Cleared on the next edit.
  const [serverError, setServerError] = useState(null);

  const open = () => {
    setDraft(field.value ?? "");
    setOther(field.otherValue ?? "");
    setServerError(null);
    setEditing(true);
  };

  const cancel = () => {
    setServerError(null);
    setEditing(false);
    setSaving(false);
  };

  const dirty =
    isFieldDirty(field.value, draft) ||
    (field.otherField ? isFieldDirty(field.otherValue, other) : false);

  // SOUND-777: the same 0–100 rule the merchant wizard applies. Only checked
  // while editing — a stored out-of-range value still READS as what it is.
  const invalid = editing ? fieldPercentError(field, draft) : null;

  const save = async () => {
    if (!dirty) return cancel();
    if (invalid) return;
    setSaving(true);
    try {
      // A row-addressable section (owners) sends a FLAT body — the URL already
      // names the row. A scalar section nests under its key so the API's
      // deep-merge leaves siblings alone. Getting this backwards on owners
      // would wholesale-replace the array.
      const rowScoped = isRowEditableSection(sectionKey);
      const payload = rowScoped
        ? buildRowFieldPayload(field.name, draft)
        : buildFieldPayload(sectionKey, field.name, draft);
      if (field.otherField) {
        // Written even when blank, so switching AWAY from "Other" clears the
        // stale companion rather than leaving it stranded on the record.
        const companion = draft === "Other" ? other : "";
        if (rowScoped) payload[field.otherField] = companion;
        else payload[sectionKey][field.otherField] = companion;
      }
      await onSave({ payload, sectionKey, rowId, fieldName: field.name });
      setServerError(null);
      setEditing(false);
    } catch (err) {
      // onSave toasts the error; keep the editor open so the typed value is
      // not silently discarded, and pin a field-specific refusal under it.
      setServerError(err?.fieldMessage || null);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") cancel();
    if (e.key === "Enter" && field.type !== "textarea") {
      e.preventDefault();
      save();
    }
  };

  const label = (
    <>
      {field.label}
      {field.required && <span className="text-destructive">*</span>}
    </>
  );

  if (editing) {
    return (
      <div className="min-w-0" onKeyDown={onKeyDown}>
        <label
          htmlFor={`edit-${field.name}`}
          className="mb-0.5 flex items-center gap-1 text-xs font-medium text-muted-foreground"
        >
          {label}
        </label>
        <EditInput
          field={field}
          value={draft}
          onChange={(v) => {
            setServerError(null);
            setDraft(v);
          }}
          other={other}
          onOtherChange={setOther}
          autoFocus
        />
        {(invalid || serverError) && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {invalid || serverError}
          </p>
        )}
        <div className="mt-1.5 flex items-center gap-1.5">
          <button
            type="button"
            className="btn btn-primary h-7 px-2 text-xs"
            onClick={save}
            disabled={saving || !dirty || Boolean(invalid)}
            title={invalid || (dirty ? "Save this field" : "No change")}
          >
            <Check className="h-3.5 w-3.5" />
            <span className="ml-1">{saving ? "Saving…" : "Save"}</span>
          </button>
          <button
            type="button"
            className="btn btn-ghost h-7 px-2 text-xs"
            onClick={cancel}
            disabled={saving}
          >
            <X className="h-3.5 w-3.5" />
            <span className="ml-1">Cancel</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group min-w-0">
      <dt className="mb-0.5 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        {label}
        {canEdit && (
          <button
            type="button"
            onClick={open}
            // Visible on hover for the mouse, and on keyboard focus so the row
            // stays reachable without one.
            className="opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
            title={`Edit ${field.label}`}
            aria-label={`Edit ${field.label}`}
          >
            <Pencil className="h-3 w-3 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </dt>
      <dd
        className={
          field.isEmpty
            ? "text-sm text-muted-foreground/50"
            : "break-words text-sm text-foreground"
        }
        title={field.isEmpty ? "No value on record" : undefined}
      >
        {field.display}
      </dd>
    </div>
  );
}
